import { Box } from "@mui/material";

/**
 * PageContainer — Phase 1 (Design Gap Audit item A) reusable component.
 *
 * Matches the redesign prototype's `.page` rule exactly:
 *   .page{padding:20px 24px 56px;max-width:1560px;width:100%}
 *
 * Root-caused gap this fixes: every existing page currently renders its
 * content directly inside AppLayout's <main>, which has no max-width, so on
 * wide monitors the app stretches edge-to-edge instead of the prototype's
 * intentional 1560px-capped, centered-feeling layout. AppLayout's <main>
 * itself is left untouched (it still spans the remaining width next to the
 * sidebar, and its own 56px topbar still goes edge-to-edge exactly like the
 * prototype's full-width .topbar) — this component only wraps each PAGE's
 * own content below the topbar, matching how the prototype separates
 * .topbar (full width) from .page (capped width) as two different rules.
 *
 * This is additive: no existing page has been wired to use it yet. Each
 * page adopts it individually in its own redesign pass (Positions,
 * Candidates, Dashboard, etc.) so that page's existing padding/layout logic
 * is reviewed and replaced deliberately, not silently changed everywhere at
 * once.
 *
 * Props:
 *   children  - the page's existing content (required)
 *   disableMaxWidth - escape hatch for a page that genuinely needs full
 *                     width (e.g. a wide kanban board); default false
 *   sx        - optional additional MUI sx overrides, merged last
 */
export default function PageContainer({
  children,
  disableMaxWidth = false,
  sx = {},
}) {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: disableMaxWidth ? "100%" : 1560,
        mx: "auto",
        px: { xs: 2, sm: 3 }, // 24px matches prototype's 24px side padding at sm+; slightly tighter on mobile
        pt: 2.5, // 20px
        pb: 7, // 56px
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
